import {
  type GetOpenShiftsRequestQuery,
  type GetOpenShiftsResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { type DateRange } from "@src/appV2/lib";
import { differenceInDays, endOfDay, startOfDay } from "date-fns";
import uniqBy from "lodash/uniqBy";
import { useMemo } from "react";

import { getIncludedOpenShiftsData } from "./getIncludedOpenShiftsData";
import { useGetOpenShifts } from "./useGetOpenShifts";
import { useGetPaginatedOpenShifts } from "./useGetPaginatedOpenShifts";

interface UseGetOpenShiftsForDatesProps {
  filter: Omit<GetOpenShiftsRequestQuery["filter"], "start">;
  /**
   * Date range to load open shifts for.
   */
  dateRange: DateRange;
  /**
   * Specific dates to load open shifts for
   */
  dates: Date[];
  enabled: boolean;
  includeOffers?: boolean;
}
/**
 * This hooks adaptively fetches open shifts data based on the date filter applied.
 * It might either use paginated query or a static query, depending what is the date filter applied.
 * 1. If the date filter is applied, it uses static query. We will just load all the shifts for the dates and won't allow for infinite scroll.
 * 2. If the date filter is not applied, it uses paginated query and allows for infinite scroll.
 */
export function useGetOpenShiftsForDates(props: UseGetOpenShiftsForDatesProps) {
  const { dates, filter, dateRange, enabled, includeOffers = false } = props;

  const staticDateFilters = dates.map((date) => ({
    gte: startOfDay(date).toISOString(),
    lte: endOfDay(date).toISOString(),
  }));

  const isDateFilterApplied = dates.length > 0;

  const include: Array<"workplace" | "rate-negotiation" | "offer" | "shift-auction"> = [
    "workplace",
    "rate-negotiation",
    "shift-auction",
  ];

  if (includeOffers) {
    include.push("offer");
  }

  // We want list view to have infinite scroll when the date filter is not active, so we fetch paginated shifts.
  const { data: paginatedOpenShiftsData, ...paginatedOpenShiftsDataResult } =
    useGetPaginatedOpenShifts(
      {
        initialDateRange: dateRange,
        filter,
        include,
      },
      { enabled: enabled && !isDateFilterApplied }
    );

  const consolidatedPaginatedOpenShiftsData: GetOpenShiftsResponse | undefined = useMemo(
    () =>
      paginatedOpenShiftsData
        ? {
            data: paginatedOpenShiftsData.pages.flatMap((page) => page.data),
            included: paginatedOpenShiftsData.pages.flatMap((page) => page.included),
          }
        : undefined,
    [paginatedOpenShiftsData]
  );

  const daysCount =
    paginatedOpenShiftsData && paginatedOpenShiftsData.pages.length > 0
      ? differenceInDays(
          paginatedOpenShiftsData.pages.at(-1)!.dateRange.endDate,
          paginatedOpenShiftsData.pages[0].dateRange.startDate
        )
      : differenceInDays(dateRange.endDate, dateRange.startDate);

  // When the date filter is active, we get a static list of shifts for the dates selected, no pagination required.
  const { data: openShiftsData, ...openShiftsDataResult } = useGetOpenShifts(
    {
      filter: {
        ...filter,
        start: staticDateFilters,
      },
      include,
    },
    { enabled: enabled && isDateFilterApplied }
  );

  const { shifts, workplacesMap, rateNegotiationsMap, offersMap, shiftAuctionsMap } =
    useMemo(() => {
      const data = isDateFilterApplied ? openShiftsData : consolidatedPaginatedOpenShiftsData;

      const shifts = data?.data;
      // The backend currently doesn't always return distinct shifts, this is a temporary fix until that is resolved
      const dedupedShifts = uniqBy(shifts, (shift) => shift.id);

      const { workplacesMap, rateNegotiationsMap, offersMap, shiftAuctionsMap } =
        getIncludedOpenShiftsData(data);

      return {
        shifts: dedupedShifts,
        workplacesMap,
        rateNegotiationsMap,
        offersMap,
        shiftAuctionsMap,
      };
    }, [openShiftsData, isDateFilterApplied, consolidatedPaginatedOpenShiftsData]);

  const isLoading = isDateFilterApplied
    ? openShiftsDataResult.isLoading
    : paginatedOpenShiftsDataResult.isLoading;
  const isError = isDateFilterApplied
    ? openShiftsDataResult.isError
    : paginatedOpenShiftsDataResult.isError;
  const isSuccess = isDateFilterApplied
    ? openShiftsDataResult.isSuccess
    : paginatedOpenShiftsDataResult.isSuccess;

  return {
    shifts,
    workplacesMap,
    rateNegotiationsMap,
    shiftAuctionsMap,
    offersMap,
    daysCount,
    isLoading,
    isError,
    isSuccess,
    pagination: isDateFilterApplied
      ? undefined
      : {
          isFetchingNextPage: paginatedOpenShiftsDataResult.isFetchingNextPage,
          hasNextPage: paginatedOpenShiftsDataResult.hasNextPage,
          fetchNextPage: paginatedOpenShiftsDataResult.fetchNextPage,
        },
  };
}
